import { useBridge } from "@/features/bridge/providers/bridge.provider";
import { CONTRACT_ADDRESSES } from "@/constants/contract-addresses.constants";
import { useQuery } from "@tanstack/react-query";

export interface TokenEligibilityResult {
  isEligible: boolean;
  reason?: "is-bless-token" | "supports-bridge-resilient-1155" | "not-supported";
  isLoading: boolean;
}

/**
 * Hook to determine if a token is selectable for bridging:
 * 1) Token is the configured BLESS token
 * 2) Token implements bridgeResilientVersion() and returns > 0
 */
export const useTokenEligibility = (tokenAddress?: string): TokenEligibilityResult => {
  const {
    bridgeControls: {
      fromChain: { originChain },
    },
  } = useBridge();

  const isBlessToken = tokenAddress?.toLowerCase() === CONTRACT_ADDRESSES.BLESS_TOKEN?.toLowerCase();

  // Always call useQuery (hooks must be called unconditionally)
  const { data: supportsBridgeResilient1155, isLoading } = useQuery({
    queryKey: ['token-eligibility-api', tokenAddress, originChain?.chainId],
    queryFn: async () => {
      if (!tokenAddress || !originChain?.chainId) return false;

      try {
        console.log(`🔍 Checking bridgeResilientVersion for token: ${tokenAddress} on chain ${originChain.chainId}`);

        const response = await fetch(
          `/api/token-eligibility/${originChain.chainId}/${tokenAddress}`
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error(`API error for token ${tokenAddress}:`, errorData);
          return false;
        }

        const data = await response.json();
        const supported =
          data.isBridgeResilient ??
          data.supportsBridgeResilient1155 ??
          data.isShiftable ??
          false;

        if (data.cached) {
          console.log(`✅ Server cache hit for token ${tokenAddress} on chain ${originChain.chainId}: ${supported}`);
        } else {
          console.log(`🔍 Server checked token ${tokenAddress} on chain ${originChain.chainId}: ${supported} (now cached for all users)`);
        }

        return supported;
      } catch (error) {
        console.error(`Failed to check token eligibility for ${tokenAddress}:`, error);
        return false;
      }
    },
    enabled: !!tokenAddress && !!originChain?.chainId && !isBlessToken,
    refetchOnWindowFocus: false,
    retry: 1, // Retry once on network failure
    staleTime: Infinity, // Never refetch - server has the cache
    gcTime: Infinity, // Keep in memory forever - server handles caching
  });

  // Rule 1: BLESS token is always selectable
  if (isBlessToken) {
    return {
      isEligible: true,
      reason: "is-bless-token",
      isLoading: false,
    };
  }

  // Rule 2: Wait for bridgeResilientVersion check
  if (isLoading) {
    return {
      isEligible: false,
      isLoading: true,
    };
  }

  if (supportsBridgeResilient1155) {
    return {
      isEligible: true,
      reason: "supports-bridge-resilient-1155",
      isLoading: false,
    };
  }

  // If none of the rules match, token is not selectable
  return {
    isEligible: false,
    reason: "not-supported",
    isLoading: false,
  };
};

/**
 * Get user-friendly message for why a token is eligible or not
 */
export const getEligibilityMessage = (result: TokenEligibilityResult): string => {
  if (result.isLoading) return "Checking token eligibility...";

  if (result.isEligible) {
    switch (result.reason) {
      case "is-bless-token":
        return "BLESS token can be bridged on any network";
      case "supports-bridge-resilient-1155":
        return "This token reports bridgeResilientVersion > 0";
      default:
        return "This token is eligible for bridging";
    }
  }

  return "Bless bridge is in Beta, not all token types are currently supported";
};
